import {
  formatBorgValue,
  hasRecordedStepTestText,
  hasSixMSTMinuteReadingData,
  hasStepTestVitalsData,
  normalizeSixMSTPayload,
  summarizeSixMSTResult,
  type SixMSTResultPayload,
  type SixMSTSummary,
  type StepTestVitalsBlock,
} from '../step-tests/stepTestsDomain';
import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';

export const get6MSTSeverityLabel = (payload: unknown): string => summarizeSixMSTResult(payload).severity;

export const get6MSTSeverityTone = (payload: unknown): string => {
  const summary = summarizeSixMSTResult(payload);
  if (summary.severity === 'Abaixo do limite inferior' || summary.severity === 'Dessaturação importante') {
    return 'bg-rose-100 text-rose-700';
  }
  if (summary.severity === 'Tolerância reduzida' || summary.severity === 'Teste interrompido') {
    return 'bg-amber-100 text-amber-700';
  }
  if (summary.severity === 'Resposta estável') {
    return 'bg-emerald-100 text-emerald-700';
  }
  return 'bg-slate-100 text-slate-700';
};

const get6MSTSeverityTextTone = (severity: string): string => {
  if (severity === 'Abaixo do limite inferior' || severity === 'Dessaturação importante') {
    return 'text-rose-700';
  }
  if (severity === 'Tolerância reduzida' || severity === 'Teste interrompido') {
    return 'text-amber-700';
  }
  if (severity === 'Resposta estável') {
    return 'text-emerald-700';
  }
  return 'text-slate-600';
};

interface MST6ResultRendererProps {
  payload: SixMSTResultPayload | null | undefined;
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

const formatComparison = (baseline: string | null, final: string | null, suffix = ''): string | null => {
  if (baseline && final) return `${baseline}${suffix} → ${final}${suffix}`;
  if (baseline) return `${baseline}${suffix} (pré-teste)`;
  if (final) return `${final}${suffix} (pós-teste)`;
  return null;
};

const formatBloodPressure = (block: StepTestVitalsBlock): string | null => {
  const values = [
    hasRecordedStepTestText(block.bp.sys) ? `PAS ${block.bp.sys}` : null,
    hasRecordedStepTestText(block.bp.dia) ? `PAD ${block.bp.dia}` : null,
  ].filter((value): value is string => value != null);
  return values.length ? values.join(' / ') : null;
};

const buildPreviewInterpretation = (
  payload: SixMSTResultPayload,
  summary: SixMSTSummary,
): string => {
  const parts: string[] = [];

  if (summary.normativeBandLabel && summary.normativeP10 != null) {
    parts.push(
      summary.belowNormativeLowerLimit
        ? 'O desempenho ficou abaixo do limite inferior esperado para a faixa etária.'
        : 'O desempenho permaneceu acima do limite inferior esperado para a faixa etária.',
    );
  }

  if (!payload.timer.completed && summary.totalSteps > 0) {
    parts.push('O teste foi interrompido antes da duração prevista.');
  }

  if (summary.minSpo2 != null) {
    if (summary.minSpo2 <= 88) {
      parts.push('Foi observada dessaturação importante durante o teste.');
    } else if (summary.spo2Drop != null && summary.spo2Drop >= 4) {
      parts.push('Foi observada queda clinicamente relevante da SpO2 durante o teste.');
    } else {
      parts.push('Não foi observada dessaturação clinicamente relevante.');
    }
  }

  if (summary.interruptionCount > 0) {
    parts.push('Houve necessidade de pausas durante a execução.');
  }

  if (!parts.length) {
    parts.push('O teste apresentou resposta funcional estável com os dados registrados.');
  }

  return resolveAssessmentInterpretationText(payload, parts.join(' '));
};

export default function MST6ResultRenderer({ payload }: MST6ResultRendererProps) {
  const normalized = normalizeSixMSTPayload(payload);
  const summary = summarizeSixMSTResult(payload);

  if (!normalized) {
    return <div className="rounded-[24px] border border-slate-100 bg-white p-6 text-sm text-slate-600">Sem payload válido para o 6MST.</div>;
  }

  const populatedMinuteReadings = normalized.perMinute.filter(hasSixMSTMinuteReadingData);
  const showMinuteHr = populatedMinuteReadings.some((entry) => hasRecordedStepTestText(entry.hr));
  const showMinuteSpo2 = populatedMinuteReadings.some((entry) => hasRecordedStepTestText(entry.spo2));
  const showMinuteSteps = populatedMinuteReadings.some((entry) => entry.totalSteps != null);
  const showMinuteNotes = populatedMinuteReadings.some((entry) => hasRecordedStepTestText(entry.notes));
  const hasVitals = hasStepTestVitalsData(normalized.baseline) || hasStepTestVitalsData(normalized.final);
  const hrComparison = formatComparison(
    hasRecordedStepTestText(normalized.baseline.hr) ? normalized.baseline.hr : null,
    hasRecordedStepTestText(normalized.final.hr) ? normalized.final.hr : null,
    ' bpm',
  );
  const spo2Comparison = formatComparison(
    hasRecordedStepTestText(normalized.baseline.spo2) ? normalized.baseline.spo2 : null,
    hasRecordedStepTestText(normalized.final.spo2) ? normalized.final.spo2 : null,
    '%',
  );
  const bloodPressureComparison = formatComparison(formatBloodPressure(normalized.baseline), formatBloodPressure(normalized.final), ' mmHg');
  const borgDyspneaComparison = formatComparison(
    normalized.baseline.borg.dyspnea != null ? formatBorgValue(normalized.baseline.borg.dyspnea) : null,
    normalized.final.borg.dyspnea != null ? formatBorgValue(normalized.final.borg.dyspnea) : null,
  );
  const borgLegFatigueComparison = formatComparison(
    normalized.baseline.borg.legFatigue != null ? formatBorgValue(normalized.baseline.borg.legFatigue) : null,
    normalized.final.borg.legFatigue != null ? formatBorgValue(normalized.final.borg.legFatigue) : null,
  );
  const interpretation = buildPreviewInterpretation(normalized, summary);
  const normativeReferenceParts = [
    summary.normativeBandLabel ? `Faixa etária: ${summary.normativeBandLabel}` : null,
    summary.normativeP10 != null ? `Limite inferior (10º percentil): ${summary.normativeP10} degraus` : null,
  ].filter((value): value is string => value != null);
  const pauseLabel = summary.interruptionCount === 1 ? '1 pausa' : `${summary.interruptionCount} pausas`;

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Teste do degrau de 6 minutos</p>
          <div className="text-6xl font-black leading-none text-slate-900">{summary.totalSteps}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">subidas em 6 minutos</div>
          <div className={`mt-5 inline-flex items-center gap-2 text-sm font-bold ${get6MSTSeverityTextTone(summary.severity)}`}>
            <span className="h-2 w-2 rounded-full bg-current" aria-hidden="true" />
            {summary.severity}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{interpretation}</p>
          </div>

          {normativeReferenceParts.length ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
              <span className="font-semibold text-slate-900">Referência normativa</span>
              <span className="mx-2 text-slate-300" aria-hidden="true">·</span>
              {normativeReferenceParts.join(' · ')}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        {summary.predictedSteps != null || summary.percentPredicted != null ? (
          <SummaryMetric
            label="Referência prevista"
            value={summary.predictedSteps != null ? `${summary.predictedSteps.toFixed(1)} degraus` : `${summary.percentPredicted?.toFixed(1)}%`}
            note={
              summary.predictedSteps != null && summary.percentPredicted != null
                ? `Resultado equivalente a ${summary.percentPredicted.toFixed(1)}% do previsto`
                : 'Equação de Albuquerque et al. 2022'
            }
          />
        ) : null}
        {summary.minSpo2 != null ? <SummaryMetric label="SpO2 mínima" value={`${summary.minSpo2.toFixed(1)}%`} note={summary.spo2Drop != null ? `Queda de ${summary.spo2Drop.toFixed(1)}%` : null} /> : null}
        {normalized.stop_report.intervals.length ? <SummaryMetric label="Pausas" value={pauseLabel} note={`${normalized.stop_report.total_stopped_fmt} de tempo parado`} /> : null}
      </div>

      <div className={`grid gap-6 ${hasVitals ? 'xl:grid-cols-2' : ''}`}>
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Protocolo registrado</div>
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            <div className="flex items-center justify-between gap-4">
              <span>Altura do degrau</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.stepHeightCm} cm</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Duração alvo</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.durationMinutes} min</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Apoio de MMSS</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.handSupportAllowed ? 'Permitido' : 'Não permitido'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Alternância de MMII</span>
              <span className="font-semibold text-slate-900">{normalized.protocol.alternatingLegsAllowed ? 'Permitida' : 'Não registrada'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Tempo executado</span>
              <span className="font-semibold text-slate-900">{Math.floor(normalized.timer.elapsed_s / 60)}:{String(normalized.timer.elapsed_s % 60).padStart(2, '0')}</span>
            </div>
          </div>
        </div>

        {hasVitals ? (
          <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Sinais vitais e Borg</div>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              {hrComparison ? <div className="flex items-center justify-between gap-4"><span>FC</span><span className="font-semibold text-slate-900">{hrComparison}</span></div> : null}
              {spo2Comparison ? <div className="flex items-center justify-between gap-4"><span>SpO2</span><span className="font-semibold text-slate-900">{spo2Comparison}</span></div> : null}
              {bloodPressureComparison ? <div className="flex items-center justify-between gap-4"><span>PA</span><span className="text-right font-semibold text-slate-900">{bloodPressureComparison}</span></div> : null}
              {borgDyspneaComparison ? <div className="flex items-center justify-between gap-4"><span>Borg dispneia</span><span className="font-semibold text-slate-900">{borgDyspneaComparison}</span></div> : null}
              {borgLegFatigueComparison ? <div className="flex items-center justify-between gap-4"><span>Borg fadiga</span><span className="font-semibold text-slate-900">{borgLegFatigueComparison}</span></div> : null}
            </div>
          </div>
        ) : null}
      </div>

      {populatedMinuteReadings.length ? (
        <div className="overflow-hidden rounded-[24px] border border-slate-100 shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
                <th className="px-5 py-4">Min</th>
                {showMinuteHr ? <th className="px-5 py-4">FC</th> : null}
                {showMinuteSpo2 ? <th className="px-5 py-4">SpO2</th> : null}
                {showMinuteSteps ? <th className="px-5 py-4">Subidas acumuladas</th> : null}
                {showMinuteNotes ? <th className="px-5 py-4">Observações</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {populatedMinuteReadings.map((entry) => (
                <tr key={`6mst-minute-${entry.minute}`}>
                  <td className="px-5 py-4 font-bold text-slate-700">{entry.minute}</td>
                  {showMinuteHr ? <td className="px-5 py-4 text-slate-700">{entry.hr ?? '—'}</td> : null}
                  {showMinuteSpo2 ? <td className="px-5 py-4 text-slate-700">{entry.spo2 ?? '—'}</td> : null}
                  {showMinuteSteps ? <td className="px-5 py-4 font-semibold text-slate-900">{entry.totalSteps ?? '—'}</td> : null}
                  {showMinuteNotes ? <td className="px-5 py-4 text-slate-700">{entry.notes ?? '—'}</td> : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {normalized.stop_report.intervals.length ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Pausas registradas</div>
          <div className="mt-4 grid gap-3 xl:grid-cols-2">
            {normalized.stop_report.intervals.map((entry, index) => (
              <div key={`6mst-stop-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <div className="font-semibold text-slate-900">{entry.reason_label || 'Motivo não informado'}</div>
                <div className="mt-1">{entry.from_fmt} → {entry.to_fmt} ({entry.duration_fmt})</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
